<script lang="ts" setup>
	import dayjs from 'dayjs'
	import type { ListContext } from '@/components/Shared/list-item.vue'
	import usePurchases from '~/services/apis/purchases-info'

	const { status = 'active', ...props } = defineProps<{
		status?: 'pending' | 'active'
		dependantName?: string
		staffName?: string
		staffId: string
		productName?: string
		loading?: boolean
		show: boolean
	}>()
	const emits = defineEmits<{
		(event: 'update:show', arg: boolean): void
		(event: 'done'): void
	}>()
	// TODO: Check for pending policies for dependants summary
	const { removePolicySummary, removePendingPolicySummary } = usePurchases()

	const isTermsOpen = shallowRef(false)
	const loadingPrice = shallowRef(false)
	const verifyingPass = shallowRef(false)

	const formData = reactive({
		terms: false,
		password: '',
		renewal_date: '',
		price: ''
	})

	const { currencyToNaira } = useCurrency()
	const dependantSummary = computed<ListContext>(() => ({
		'New Plan Premium': {
			value: formData.price || '---',
			pClass: 'text-primary-2 '
		},
		'Payment Date': (formData.renewal_date && dayjs(formData.renewal_date).format('DD MMM, YYYY')) || '--'
	}))

	const submit = () => resolve(() => emits('done'), verifyingPass)

	watch(
		() => props.show,
		async active => {
			if (active) {
				resolve(async () => {
					const result =
						status === 'pending'
							? await removePendingPolicySummary([props.staffId])
							: await removePolicySummary([props.staffId])
					if (result?.data) {
						formData.renewal_date =
							result.data.plan?.actual_renewal_date || result.data?.plan_renewal_date || ''
						formData.price = currencyToNaira(result.data.new_plan_premium)
					}
				}, loadingPrice)
			}
			formData.terms = false
		}
	)
</script>

<template>
	<div>
		<dialogs-error type="warning" :show="show" @close="emits('update:show', false)">
			<shared-article-description class="bg-gray-50" title="Remove staff dependant">
				<template #paragraph>
					<p class="leading-loose">
						This staff dependant
						<span class="font-medium text-primary-2">{{ dependantName }}</span> under
						<span class="font-medium text-gray-600">{{ staffName || 'N/A' }}</span>
						will no longer have access to care once deactivated from your
						<span class="font-medium capitalize">{{ productName || 'N/A' }}</span> Plan. Your total premium
						will also be updated upon plan renewal.
					</p>
				</template>
			</shared-article-description>

			<shared-list-item
				class="my-8 rounded border border-purple-200 bg-purple-50"
				:lists="dependantSummary"
				:loading="loadingPrice"
			/>

			<template #button>
				<span> </span>
			</template>
			<a-form class="block" :model="formData" @finish="submit">
				<a-form-item required name="terms">
					<div class="inline-flex items-start gap-x-3">
						<a-checkbox v-model:checked="formData.terms" />
						<span class="block pt-1 text-sm text-gray-600">
							I have read and I accept the
							<span
								aria-role="button"
								aria-label="terms and condition"
								class="cursor-pointer font-medium text-primary-2"
								@click="() => (isTermsOpen = true)"
							>
								Terms & Conditions
							</span>
						</span>
					</div>
				</a-form-item>

				<div class="mt-8 flex items-center justify-between gap-x-8">
					<button
						type="button"
						class="default-btn w-full justify-center bg-gray-200"
						@click="formData.terms = false"
					>
						Back
					</button>

					<a-button
						:loading="loading || verifyingPass"
						html-type="submit"
						class="default-btn w-full justify-center bg-primary-2 !font-medium !text-white"
						:disabled="!formData.terms || verifyingPass"
					>
						Remove Dependant
					</a-button>
				</div>
			</a-form>
		</dialogs-error>
		<dialogs-terms-and-condition v-model:show="isTermsOpen" />
	</div>
</template>
